import { useEffect, useRef, useState } from 'react';
import { CalendarRange, MonitorCog, Phone, PhoneForwarded } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import type { TaskWithRelations } from '@vagusx/clients';
import type { RealtimeProvider } from '@vagusx/shared';
import { AGENTS, E164_PATTERN, PERSONAS, agentsFor, realize } from '@vagusx/shared';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { SheetActions, SheetBody, SheetCancel } from '@/components/custom/sheet-layout';
import { usePatient } from '@/hooks/use-patients';
import { useSchedulingScope } from '@/scheduling/hooks/use-scheduling-scope';
import { formatPhone, pickInitialDialNumber } from '@/lib/format';
import { CallPanel } from '@/common/phone/CallPanel';
import { CallEnded } from '@/common/phone/CallEnded';
import type { TestCallStartOptions } from '@/common/phone/use-test-call';
import { InteractionDetailSheet } from '@/components/interactions/InteractionDetailSheet';
import { queryKeys } from '@/lib/query-keys';
import { canResolveReferralDocument, formatPatientType } from '@/hooks/use-referrals';
import { OfferedSlotsDialog } from '@/scheduling/components/OfferedSlotsDialog';
import { ReferralAppointmentTypeCard } from '../ReferralAppointmentTypeCard';
import { TextToScheduleDrawer } from './TextToScheduleDrawer';

/**
 * Realtime model options shown in the configuring-phase picker. Each
 * entry's `value` is a `<provider>/<model>` key used as the Select's
 * controlled value; on change the picker splits it back into the two
 * fields the call request body expects.
 *
 * Add a new model = one entry here. The LiveKit worker doesn't need
 * to know the catalog — it just consumes `realtimeProvider` (typed)
 * and `realtimeModel` (free-form string) from the request body.
 */
const REALTIME_MODEL_OPTIONS: ReadonlyArray<{
  value: string;
  provider: RealtimeProvider;
  model: string;
  label: string;
  hint: string;
}> = [
  {
    value: 'openai/gpt-realtime-2',
    provider: 'openai',
    model: 'gpt-realtime-2',
    label: 'OpenAI · gpt-realtime-2',
    hint: 'GPT-5-class reasoning, 128k ctx',
  },
  {
    value: 'openai/gpt-realtime',
    provider: 'openai',
    model: 'gpt-realtime',
    label: 'OpenAI · gpt-realtime',
    hint: 'Current GA',
  },
  {
    value: 'xai/grok-voice-think-fast-1.0',
    provider: 'xai',
    model: 'grok-voice-think-fast-1.0',
    label: 'xAI · grok-voice-think-fast-1.0',
    hint: 'With reasoning',
  },
  {
    value: 'xai/grok-voice-fast-1.0',
    provider: 'xai',
    model: 'grok-voice-fast-1.0',
    label: 'xAI · grok-voice-fast-1.0',
    hint: 'Speed-optimized, no reasoning',
  },
  {
    value: 'google/gemini-2.5-flash-native-audio-preview-12-2025',
    provider: 'google',
    model: 'gemini-2.5-flash-native-audio-preview-12-2025',
    label: 'Google · gemini-2.5-native-audio (12-2025)',
    hint: 'Native audio + affective dialog + proactive',
  },
  {
    value: 'google/gemini-live-2.5-flash-preview-native-audio-09-2025',
    provider: 'google',
    model: 'gemini-live-2.5-flash-preview-native-audio-09-2025',
    label: 'Google · gemini-2.5-native-audio (09-2025)',
    hint: 'Native audio, prior preview build',
  },
];

const DEFAULT_MODEL_VALUE = 'openai/gpt-realtime-2';

interface ScheduleAppointmentDrawerProps {
  task: TaskWithRelations;
  /**
   * Controlled open state. When provided, the component is controlled and
   * renders ONLY the sheet — the inline trigger (appointment-type card +
   * "Call to schedule" button) is suppressed. Used by the Referrals list to
   * open the same scheduling panel from a row action, where the surrounding
   * UI (appointment type, etc.) already lives in the table row.
   *
   * Omit both props for the default uncontrolled mode used on the Task
   * Overview, where the component renders its own trigger and manages open
   * state internally.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Action drawer: "Call to Schedule Appointment". Wraps the voice
 * test-call panel (`POST /api/browser-call/test`); the server resolves
 * the referral document from the task and creates the
 * interaction_tasks link so the call lands on the task's activity
 * timeline.
 *
 * The drawer no longer fetches email attachments client-side — the
 * server-side `TaskDocumentResolver` owns that logic now. We just
 * pass `taskId` and the API responds with a clear error if the task
 * has no resolvable document.
 *
 * Button enable: only checks that the task has an objectType that
 * could resolve to a document (`document` or `email`). Actual
 * resolution happens server-side; if it fails the user sees an error
 * via the existing CallPanel error path.
 *
 * Post-call: the sheet stays OPEN with the CallEnded screen so the
 * user can jump straight to the post-call view, and we invalidate
 * the task's timeline cache so the activity tab picks up the new
 * interaction without a 10-minute wait.
 */
/**
 * The call modes: how the call is placed. Both run the v2 workflow engine, the
 * only one there is (ADR-310).
 */
const CALL_MODES = {
  'browser-test': {
    transport: 'browser-test',
    icon: MonitorCog,
    label: 'Browser test',
    hint: 'Talk to the agent yourself',
  },
  'sip-outbound': {
    transport: 'sip-outbound',
    icon: PhoneForwarded,
    label: 'Real phone',
    hint: 'Agent dials over SIP; you monitor',
  },
} as const satisfies Record<
  string,
  {
    transport: 'browser-test' | 'sip-outbound';
    icon: typeof MonitorCog;
    label: string;
    hint: string;
  }
>;

type CallMode = keyof typeof CALL_MODES;

/** Render order. Explicit so adding a mode does not silently reorder the row. */
const CALL_MODE_ORDER: readonly CallMode[] = ['browser-test', 'sip-outbound'];

function isCallMode(value: string): value is CallMode {
  return value in CALL_MODES;
}

export function ScheduleAppointmentDrawer({
  task,
  open: controlledOpen,
  onOpenChange,
}: ScheduleAppointmentDrawerProps): React.ReactElement {
  const queryClient = useQueryClient();
  // Controlled vs uncontrolled: when `open` is passed the parent owns the
  // state (Referrals list); otherwise we own it (Task Overview).
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (next: boolean): void => {
    if (isControlled) onOpenChange?.(next);
    else setInternalOpen(next);
  };
  const [callKey, setCallKey] = useState(0);
  // Inline interaction-detail sheet driven from CallEnded's "Open
  // post-call view" CTA. Replaces the legacy navigation to
  // `/activity/$interactionId` so the user stays on the task page (the
  // route is being deprecated — see InteractionDetailSheet docblock).
  const [detailInteractionId, setDetailInteractionId] = useState<string | null>(null);

  // UI phase inside the open sheet:
  //  - 'configuring': pre-call picker for which realtime provider to use
  //                   (OpenAI gpt-realtime vs xAI grok-voice-think-fast).
  //                   Single source of friction before the call, so the
  //                   tester picks intentionally instead of toggling env.
  //  - 'calling':     CallPanel mounted, live LiveKit room
  //  - 'ended':       CallPanel disconnected — show post-call screen
  // We DO NOT auto-close on call end. The user reads the summary,
  // opens the post-call view, or starts another call from the same sheet.
  type CallPhase =
    | { phase: 'configuring' }
    | { phase: 'calling' }
    | { phase: 'ended'; interactionId: string | null };
  const [callPhase, setCallPhase] = useState<CallPhase>({ phase: 'configuring' });

  // Realtime model picker — defaults to the newest OpenAI option.
  // Persists across back-to-back calls in the same sheet open so the
  // tester doesn't have to re-pick after every call. Encoded as
  // `<provider>/<model>` so a single Select value drives both fields.
  const [modelValue, setModelValue] = useState<string>(DEFAULT_MODEL_VALUE);

  // Transport: browser-test (operator talks via mic) vs sip-outbound
  // (agent dials a real phone, operator monitors listen-only). Defaults
  // to the browser test so the one-click flow never rings a phone.
  const [callMode, setCallMode] = useState<CallMode>('browser-test');
  const transport = CALL_MODES[callMode].transport;
  /**
   * WHO speaks and WHAT carries it, for this call (§5). Seeded from the
   * catalog's first entries so the common case is one click, not three.
   */
  const [personaId, setPersonaId] = useState<string>(PERSONAS[0]?.id ?? '');
  const [agentId, setAgentId] = useState<string>(AGENTS[0]?.id ?? '');
  const chosenPersona = PERSONAS.find((p) => p.id === personaId) ?? null;
  /**
   * Agents this persona can actually run on.
   *
   * A voice with no realization on an agent is refused at dispatch rather than
   * substituted, so offering the pairing here would be offering a call that
   * cannot be placed. Availability is shown, never silently corrected.
   */
  const offerableAgents = chosenPersona ? agentsFor(chosenPersona) : AGENTS;
  const pairing = chosenPersona ? (AGENTS.find((a) => a.id === agentId) ?? null) : null;
  const declined =
    chosenPersona && pairing
      ? ((realize(chosenPersona, pairing) as { declined?: readonly string[] }).declined ?? [])
      : [];

  // SIP destination number, editable. Pre-filled with the patient's
  // on-file number but overridable so a test call can be pointed at the
  // operator's own phone instead of ringing a real patient. We fetch the
  // patient here (same hook the PatientPanel uses) purely to seed this
  // default — the API re-resolves the patient server-side from the task.
  const { data: patient } = usePatient(task.patientId ?? undefined);
  // Server-derived dial-ready numbers (E.164, primary first), split out of a
  // possibly multi-number patients.phone field. Drives the default + the
  // override picker below so a patient with several numbers is still callable.
  const dialablePhones = patient?.dialablePhones ?? [];
  const [toPhone, setToPhone] = useState<string>('');
  // Seed the field once the patient loads, but never clobber an edit the
  // operator already made (only fill when still blank). Prefer the server's
  // primary dialable number (already E.164, and split out of any multi-number
  // field) so the field starts valid; fall back to a best-effort E.164 of the
  // raw value, then the raw value so inline validation can flag it.
  useEffect(() => {
    const seed = pickInitialDialNumber(patient?.dialablePhones, patient?.phone);
    if (seed) setToPhone((cur) => cur || seed);
  }, [patient?.dialablePhones, patient?.phone]);

  // ── Scheduling scope: provider + appointment type + location. The state +
  // effects (recommendation seed, reset-on-provider, auto-select-primary) live in
  // the shared `useSchedulingScope` hook so this drawer, the text drawer, and the
  // /sms/test tool can't drift. This drawer renders its own richer fields (EHR
  // warnings + slot preview) but reads/writes the same scope.
  const couldResolveDocument = canResolveReferralDocument(task);
  const {
    providerId,
    setProviderId,
    appointmentTypeId,
    setAppointmentTypeId,
    locationId,
    setLocationId,
    providers,
    appointmentTypes,
    locationOptions,
    providerLocationsLoading,
    selectedProvider,
    selectedType,
    ready: schedulingReady,
  } = useSchedulingScope(task);

  // Warm pulls nothing for a provider that isn't linked to the EHR — warn the
  // operator up front rather than letting the agent find zero slots mid-call.
  const providerNotEhrMapped = !!selectedProvider && !selectedProvider.ehrProviderId;
  // A provider with no clinic locations linked can't be scheduled (nothing to
  // warm/book against). Surface it explicitly instead of leaving Start silently
  // disabled — the location Select is hidden for the 0/1 cases, so without this
  // the operator gets no actionable reason.
  const providerHasNoLocation =
    !!providerId && !providerLocationsLoading && locationOptions.length === 0;
  // "Preview offered slots" popup — shows the slots the agent would offer for
  // the current selection on a navigable week grid. Gated on the same readiness
  // as the call (provider + type + location).
  const [slotsPreviewOpen, setSlotsPreviewOpen] = useState(false);
  const selectedTypeLabel = selectedType?.name;
  const isNewPatient = formatPatientType(task.labels) === 'New';
  // REALTIME_MODEL_OPTIONS is a literal non-empty array at module scope,
  // so [0] is statically known to exist — the non-null assertion silences
  // TS's `noUncheckedIndexedAccess` rather than papering over a real
  // narrowing gap.
  const selectedOption =
    REALTIME_MODEL_OPTIONS.find((o) => o.value === modelValue) ?? REALTIME_MODEL_OPTIONS[0]!;

  // Idempotency key has to be stable across the StrictMode double-mount
  // but unique per call session — bump `callKey` to start a fresh call.
  const idempotencyKeyRef = useRef<string>(crypto.randomUUID());

  // Reset call state on every closed→open transition. Lives in an effect
  // (not the open handler) so it fires for BOTH the uncontrolled trigger
  // and a controlled parent that flips `open` to true directly.
  const prevOpenRef = useRef(open);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (open && !wasOpen) {
      idempotencyKeyRef.current = crypto.randomUUID();
      setCallKey((k) => k + 1);
      setCallPhase({ phase: 'configuring' });
    }
  }, [open]);

  const handleOpenChange = (next: boolean): void => {
    setOpen(next);
  };

  const startCallFromConfig = (): void => {
    idempotencyKeyRef.current = crypto.randomUUID();
    setCallKey((k) => k + 1);
    setCallPhase({ phase: 'calling' });
  };

  const handleCallEnded = (interactionId: string | null): void => {
    setCallPhase({ phase: 'ended', interactionId });
    // The interaction was just written by the agent — bust the
    // task-timeline cache so the activity tab refetches and shows the
    // new call instead of waiting out the 10-minute staleTime.
    queryClient.invalidateQueries({ queryKey: queryKeys.tasks.timeline(task.id) });
  };

  const startAnotherCall = (): void => {
    // Skip 'configuring' on follow-up calls — the picker selection from
    // the first call is kept (state hasn't reset) and the tester usually
    // wants to re-run the same configuration. They can change it via
    // the picker after returning to configuring (closing & reopening).
    idempotencyKeyRef.current = crypto.randomUUID();
    setCallKey((k) => k + 1);
    setCallPhase({ phase: 'calling' });
  };

  const trimmedToPhone = toPhone.trim();
  const isSip = transport === 'sip-outbound';
  // Validate E.164 client-side so the operator gets an inline hint instead
  // of a raw server 422, and so we never dial a malformed number.
  const toPhoneValid = E164_PATTERN.test(trimmedToPhone);
  const startOptions: TestCallStartOptions = {
    callType: 'schedule_referral',
    // CATALOG ids only — never a provider voice.
    ...(personaId && agentId ? { personaId, agentId } : {}),
    taskId: task.id,
    preVerified: true,
    direction: 'outbound',
    transport,
    // Operator-selected scheduling scope — drives pre-dial warm + the agent's
    // provider lock + booking duration. Gated by `schedulingReady` (the Start
    // button is disabled until all three are chosen).
    ...(providerId ? { providerId } : {}),
    ...(appointmentTypeId ? { appointmentTypeId } : {}),
    ...(locationId ? { locationId } : {}),
    // Override only on the SIP path (browser-test ignores it). Sent only
    // when valid — the button is disabled otherwise.
    ...(isSip && toPhoneValid ? { toPhoneOverride: trimmedToPhone } : {}),
    // NOTHING HERE FOR THE BROWSER-TEST SMS DESTINATION, deliberately
    // (ADR-197). A browser test has no line, so the agent asks the operator
    // for a number mid-call and reads it back — which is the same thing a
    // receptionist does, and one less field to fill in correctly.
    idempotencyKey: idempotencyKeyRef.current,
    realtimeProvider: selectedOption.provider,
    realtimeModel: selectedOption.model,
  };

  return (
    <>
      {/* Inline trigger — uncontrolled (Task Overview) mode only. When the
          component is controlled (Referrals list row action), the parent owns
          opening and the appointment type already shows in the table, so we
          render only the sheet below. */}
      {!isControlled && (
        <>
          {/* Preview of the appointment type the matcher would pick for this
              referral — shown above the call action so the operator can verify
              the selection before dialing. Renders nothing for tasks with no
              resolvable referral document. */}
          <ReferralAppointmentTypeCard task={task} />

          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={() => handleOpenChange(true)}
              disabled={!couldResolveDocument}
              title={
                couldResolveDocument
                  ? 'Start a voice call to schedule an appointment'
                  : 'This task has no document source — open a referral or email task to start a call'
              }
            >
              <Phone className="mr-1.5 h-4 w-4" />
              Call to schedule appointment
            </Button>
            <TextToScheduleDrawer task={task} />
          </div>
        </>
      )}

      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent className="w-full sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>Call to schedule appointment</SheetTitle>
            <SheetDescription>
              Live voice call. The agent will use the linked referral as context. Test it in your
              browser, or place a real phone call over SIP.
            </SheetDescription>
          </SheetHeader>
          {open && callPhase.phase === 'configuring' && (
            <>
              {/* The config form is taller than the sheet on short viewports
                  (and grows with the SIP-only fields), so SheetBody owns the
                  scroll. It is a plain block, not a flex column: flex
                  children would shrink to fit instead of overflowing. */}
              <SheetBody>
                <div className="flex flex-col gap-4 py-4">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="schedule-provider-select">Provider</Label>
                    <Select value={providerId} onValueChange={setProviderId}>
                      <SelectTrigger id="schedule-provider-select" className="w-full">
                        <SelectValue placeholder="Select a provider" />
                      </SelectTrigger>
                      <SelectContent>
                        {providers.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.displayName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {providerHasNoLocation ? (
                      <p className="text-destructive text-xs">
                        This provider has no clinic location linked — link one in Providers settings
                        before scheduling.
                      </p>
                    ) : providerNotEhrMapped ? (
                      <p className="text-destructive text-xs">
                        This provider isn't linked to the EHR — no open slots will be pulled, so the
                        agent may not find any times.
                      </p>
                    ) : (
                      <p className="text-muted-foreground text-xs">
                        The agent offers only this provider's open times.
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label htmlFor="schedule-type-select">Appointment type</Label>
                    <Select value={appointmentTypeId} onValueChange={setAppointmentTypeId}>
                      <SelectTrigger id="schedule-type-select" className="w-full">
                        <SelectValue placeholder="Select an appointment type" />
                      </SelectTrigger>
                      <SelectContent>
                        {(appointmentTypes ?? []).map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.name}
                            <span className="text-muted-foreground ml-2 text-xs">
                              {t.durationMinutes} min
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-muted-foreground text-xs">
                      Sets the booking duration. Defaults to the matched type below — change it if
                      needed.
                    </p>
                  </div>

                  {locationOptions.length > 1 && (
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="schedule-location-select">Location</Label>
                      <Select value={locationId} onValueChange={setLocationId}>
                        <SelectTrigger id="schedule-location-select" className="w-full">
                          <SelectValue placeholder="Select a location" />
                        </SelectTrigger>
                        <SelectContent>
                          {locationOptions.map((l) => (
                            <SelectItem key={l.location.id} value={l.location.id}>
                              {l.location.name}
                              {l.isPrimary ? (
                                <span className="text-muted-foreground ml-2 text-xs">primary</span>
                              ) : null}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="flex flex-col gap-2">
                    <Label>Call mode</Label>
                    <ToggleGroup
                      type="single"
                      value={callMode}
                      onValueChange={(v) => {
                        // Radix fires '' when the active item is re-clicked;
                        // ignore so a mode is always selected.
                        if (isCallMode(v)) setCallMode(v);
                      }}
                      // Two columns, one per transport. A single row clipped the
                      // hints mid-word.
                      className="grid grid-cols-2 gap-2"
                    >
                      {CALL_MODE_ORDER.map((mode) => {
                        const { icon: Icon, label, hint } = CALL_MODES[mode];
                        return (
                          <ToggleGroupItem
                            key={mode}
                            value={mode}
                            variant="outline"
                            className="h-auto flex-col items-start gap-0.5 px-3 py-2"
                          >
                            <span className="flex items-center gap-1.5 font-medium">
                              <Icon className="h-3.5 w-3.5" />
                              {label}
                            </span>
                            <span className="text-muted-foreground text-xs font-normal">
                              {hint}
                            </span>
                          </ToggleGroupItem>
                        );
                      })}
                    </ToggleGroup>
                  </div>

                  {/* WHO speaks, and WHAT carries it (§5). */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="v2-persona">Persona</Label>
                      <Select value={personaId} onValueChange={setPersonaId}>
                        <SelectTrigger id="v2-persona" className="w-full" aria-label="Persona">
                          <SelectValue placeholder="Who speaks" />
                        </SelectTrigger>
                        <SelectContent>
                          {PERSONAS.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="v2-agent">Agent</Label>
                      <Select value={agentId} onValueChange={setAgentId}>
                        <SelectTrigger id="v2-agent" className="w-full" aria-label="Agent">
                          <SelectValue placeholder="What runs it" />
                        </SelectTrigger>
                        <SelectContent>
                          {AGENTS.map((a) => {
                            // Offered but not selectable when this persona has
                            // no realization on it — the reason is visible,
                            // which is the whole point of not hiding the row.
                            const available = offerableAgents.some((o) => o.id === a.id);
                            return (
                              <SelectItem key={a.id} value={a.id} disabled={!available}>
                                {a.displayName}
                                {available ? '' : ' — no voice for this stack'}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                    {declined.length > 0 && (
                      <p className="text-muted-foreground col-span-2 text-xs">
                        This agent cannot honor: {declined.join(', ')}. The persona keeps the value;
                        the agent declines it.
                      </p>
                    )}
                  </div>

                  {isSip && (
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="sip-to-phone">Number to call</Label>
                      {/* Patient has several numbers on file (e.g. home + work,
                        split out of one field): let the operator pick which to
                        dial. The first is pre-selected as the primary; the
                        free-text field below still allows any override. */}
                      {dialablePhones.length > 1 && (
                        <Select
                          value={dialablePhones.includes(trimmedToPhone) ? trimmedToPhone : ''}
                          onValueChange={(v) => setToPhone(v)}
                        >
                          <SelectTrigger className="w-full" aria-label="Patient's numbers">
                            <SelectValue placeholder="Pick one of the patient's numbers" />
                          </SelectTrigger>
                          <SelectContent>
                            {dialablePhones.map((num, i) => (
                              <SelectItem key={num} value={num}>
                                {formatPhone(num)}
                                {i === 0 ? (
                                  <span className="text-muted-foreground ml-2 text-xs">
                                    primary
                                  </span>
                                ) : null}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <Input
                        id="sip-to-phone"
                        type="tel"
                        inputMode="tel"
                        placeholder="+16505551234"
                        value={toPhone}
                        onChange={(e) => setToPhone(e.target.value)}
                        aria-invalid={!!trimmedToPhone && !toPhoneValid}
                      />
                      {trimmedToPhone && !toPhoneValid ? (
                        <p className="text-destructive text-xs">
                          Enter a valid E.164 number, e.g. +16505551234.
                        </p>
                      ) : (
                        <p className="text-muted-foreground text-xs">
                          E.164 format. Defaults to the patient's number on file — change it to your
                          own number while testing so a real patient isn't called.
                        </p>
                      )}
                    </div>
                  )}

                  <div className="flex flex-col gap-2">
                    <Label htmlFor="realtime-model-select">Voice model</Label>
                    <Select value={modelValue} onValueChange={setModelValue}>
                      <SelectTrigger id="realtime-model-select" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectLabel>OpenAI</SelectLabel>
                          {REALTIME_MODEL_OPTIONS.filter((o) => o.provider === 'openai').map(
                            (o) => (
                              <SelectItem key={o.value} value={o.value}>
                                {o.label}
                                <span className="text-muted-foreground ml-2 text-xs">{o.hint}</span>
                              </SelectItem>
                            )
                          )}
                        </SelectGroup>
                        <SelectGroup>
                          <SelectLabel>xAI</SelectLabel>
                          {REALTIME_MODEL_OPTIONS.filter((o) => o.provider === 'xai').map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                              <span className="text-muted-foreground ml-2 text-xs">{o.hint}</span>
                            </SelectItem>
                          ))}
                        </SelectGroup>
                        <SelectGroup>
                          <SelectLabel>Google</SelectLabel>
                          {REALTIME_MODEL_OPTIONS.filter((o) => o.provider === 'google').map(
                            (o) => (
                              <SelectItem key={o.value} value={o.value}>
                                {o.label}
                                <span className="text-muted-foreground ml-2 text-xs">{o.hint}</span>
                              </SelectItem>
                            )
                          )}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    <p className="text-muted-foreground text-xs">
                      Selection applies only when the LiveKit session runs the realtime stack.
                    </p>
                  </div>
                </div>
              </SheetBody>
              <SheetActions
                start={
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setSlotsPreviewOpen(true)}
                    disabled={!schedulingReady}
                    title={
                      schedulingReady
                        ? 'See the times the agent would offer'
                        : 'Pick a provider, appointment type, and location first'
                    }
                  >
                    <CalendarRange className="mr-1.5 h-4 w-4" />
                    Preview offered slots
                  </Button>
                }
              >
                <SheetCancel />
                <Button
                  onClick={startCallFromConfig}
                  disabled={!schedulingReady || (isSip && !toPhoneValid)}
                  title={
                    !schedulingReady
                      ? 'Pick a provider, appointment type, and location first'
                      : undefined
                  }
                >
                  <Phone className="mr-1.5 h-4 w-4" />
                  {isSip ? 'Place call' : 'Start call'}
                </Button>
              </SheetActions>
            </>
          )}
          {/* Body fills the remaining sheet height via flex, NOT a
              calc(100vh - magic) — the sheet has its own header padding
              + flex gap, so the calc form bottoms past the viewport and
              the inner conversation scroller never receives a constrained
              height. `flex-1 min-h-0` is the canonical way to make a
              child of a flex-col parent take the rest of the space and
              still let nested overflow-auto scrollers work. */}
          {open && callPhase.phase !== 'configuring' && (
            <div className="flex min-h-0 flex-1 flex-col px-4 pb-4">
              {callPhase.phase === 'calling' && (
                <CallPanel
                  key={callKey}
                  options={startOptions}
                  onEnd={handleCallEnded}
                  peerLabel={isSip ? formatPhone(trimmedToPhone) : undefined}
                />
              )}
              {callPhase.phase === 'ended' && (
                <CallEnded
                  interactionId={callPhase.interactionId}
                  onStartNew={startAnotherCall}
                  // Open the detail inline rather than navigating away —
                  // keeps the schedule sheet in flight in case the user
                  // wants to start another call after reviewing.
                  onViewDetail={(id) => setDetailInteractionId(id)}
                />
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
      <InteractionDetailSheet
        interactionId={detailInteractionId}
        onOpenChange={(next) => {
          if (!next) setDetailInteractionId(null);
        }}
      />
      <OfferedSlotsDialog
        open={slotsPreviewOpen}
        onOpenChange={setSlotsPreviewOpen}
        providerId={providerId}
        appointmentTypeId={appointmentTypeId}
        locationId={locationId}
        providerLabel={selectedProvider?.displayName}
        typeLabel={selectedTypeLabel}
        durationMinutes={selectedType?.durationMinutes}
        isNewPatient={isNewPatient}
      />
    </>
  );
}
